'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { VolumeIcon, Volume2Icon, PlayIcon } from 'lucide-react'
import { isMobile, browserName } from 'react-device-detect'
import { useLocale, useTranslations } from 'next-intl'

import { cn } from '@/lib/utils'
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'

const VIDEO_URL = 'https://heyboss.heeyo.ai/assets/v3_intro_video_RiQ6I90Z.mp4'
const POSTER_URL =
  'https://heyboss.heeyo.ai/assets/v3_intro_video_poster__fXi4KdN.jpg'

export default function IntroVideoModal({
  muted = false,
}: {
  muted?: boolean
} = {}) {
  const locale = useLocale()
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isMuted, setIsMuted] = useState(muted)
  const isWechatBrowser = isMobile && browserName === 'WeChat'
  const [isPlaying, setIsPlaying] = useState(!isWechatBrowser)
  const t = useTranslations('modals.intro-video')

  // const videoUrl = useMemo(() => {
  //   return `https://heyboss.heeyo.ai/ai_ceo_videos/${locale}.mp4?v=2`
  // }, [locale])

  // useEffect(() => {
  //   localStorage.setItem('has_intro_video_played_v2', 'true')
  // }, [])

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted
      setIsMuted(!isMuted)
    }
  }

  const togglePlay = () => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.muted = false
        setIsMuted(false)
        videoRef.current.play()
        setIsPlaying(true)
      } else {
        videoRef.current.pause()
        setIsPlaying(false)
      }
    }
  }

  return (
    <DialogContent className="sm:max-w-[800px]">
      <DialogHeader>
        <DialogTitle className="text-2xl font-black">{t('title')}</DialogTitle>
        <DialogDescription>{t('description')}</DialogDescription>
      </DialogHeader>

      <div className="relative">
        <video
          ref={videoRef}
          src={VIDEO_URL}
          poster={POSTER_URL}
          className="w-full rounded-lg aspect-video"
          autoPlay={!isWechatBrowser}
          muted={isMuted}
          loop
          playsInline
        >
          Your browser does not support the video tag.
        </video>

        {isWechatBrowser && !isPlaying && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/20">
            <Button
              variant="secondary"
              size="icon"
              className="w-16 h-16 rounded-full bg-white/80 hover:bg-white/90 transition-all"
              onClick={togglePlay}
            >
              <PlayIcon className="h-8 w-8" />
            </Button>
          </div>
        )}

        <div className="absolute bottom-4 left-4 flex gap-2">
          <Button
            variant="secondary"
            size="icon"
            className={cn(
              'bg-white/80 hover:bg-white/90 transition-all ring-brand focus-visible:ring-brand',
              isMuted && 'animate-pulse ring-2',
            )}
            onClick={toggleMute}
          >
            {isMuted ? (
              <VolumeIcon className="h-4 w-4" />
            ) : (
              <Volume2Icon className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
    </DialogContent>
  )
}
